import type { TowerCounts, TowerDriver, TowerShipment } from "../../types/controlTower";
import { AlertScope, inScope } from "./monitorAlerts";
import { findLatestDeliveredShipment } from "./latestDelivery";

// Conteos de una fila de Monitoreo a partir de sus envíos, con el mismo
// criterio que el backend (controlTower.logic.js, addShipment). Se usan para
// mostrar solo el período elegido sin volver a pedir el tablero.
function countShipments(shipments: TowerShipment[]): TowerCounts {
  const counts: TowerCounts = {
    total: 0,
    delivered: 0,
    inTransit: 0,
    pending: 0,
    waiting: 0,
    incident: 0,
    cancelled: 0,
    other: 0,
    flexTotal: 0,
    delayed: 0,
    rescheduled: 0,
    retries: 0,
    flexInTransit: 0,
    nonFlexInTransit: 0,
    unknownShipping: 0,
    previousDays: 0,
    flexPreviousDays: 0,
    nonFlexPreviousDays: 0,
    carryover: 0,
    collections: 0,
    collectionCents: 0,
    collectedCents: 0,
    exchanges: 0,
    pickups: 0,
  };
  for (const s of shipments) {
    counts.total++;
    const status = s.status as keyof TowerCounts;
    if (status in counts) counts[status]++;
    if (s.waiting) counts.waiting++;
    if (s.retry) counts.retries++;
    if (s.fromPreviousDays) {
      counts.previousDays++;
      if (s.shippingUniverse === "FLEX") counts.flexPreviousDays++;
      if (s.shippingUniverse === "NON_FLEX") counts.nonFlexPreviousDays++;
    }
    if (s.carryover) counts.carryover++;
    if (s.shippingUniverse === "FLEX") counts.flexTotal++;
    if (s.status === "inTransit" && s.shippingUniverse === "FLEX") counts.flexInTransit++;
    if (s.status === "inTransit" && s.shippingUniverse === "NON_FLEX") counts.nonFlexInTransit++;
    if (s.shippingUniverse === "UNKNOWN") counts.unknownShipping++;
    if (s.collection && s.collection > 0 && s.status !== "cancelled") {
      const cents = Math.round(s.collection * 100);
      counts.collections++;
      counts.collectionCents += cents;
      if (s.status === "delivered") counts.collectedCents += cents;
    }
    if (s.management === "exchange") counts.exchanges++;
    if (s.management === "pickup") counts.pickups++;
  }
  return counts;
}

export type PeriodRow = {
  driver: TowerDriver;
  shipments: TowerShipment[];
  // En camino de días anteriores (sin movimiento hoy), por tipo. En "Hoy" no
  // están en los conteos; en "Con días anteriores", sí.
  previous: { total: number; inTransit: number; flex: number; nonFlex: number };
};

function previousInTransit(shipments: TowerShipment[]): PeriodRow["previous"] {
  const carried = shipments.filter((s) => s.carryover && s.status === "inTransit");
  return {
    total: shipments.filter((s) => s.carryover).length,
    inTransit: carried.length,
    flex: carried.filter((s) => s.shippingUniverse === "FLEX").length,
    nonFlex: carried.filter((s) => s.shippingUniverse === "NON_FLEX").length,
  };
}

// Fila de un chofer en una zona para el período: con "withPrevious" es la
// fila del backend; con "today" se recalcula con lo colectado o movido hoy.
export function periodRow(
  driver: TowerDriver,
  zoneShipments: TowerShipment[],
  scope: AlertScope,
  today: string,
): PeriodRow {
  const previous = previousInTransit(zoneShipments);
  if (scope === "withPrevious") return { driver, shipments: zoneShipments, previous };
  const shipments = zoneShipments.filter((s) => inScope(s, scope, today));
  const counts = countShipments(shipments);
  const lastDelivery = findLatestDeliveredShipment(shipments)?.stateDate ?? null;
  return {
    driver: {
      ...driver,
      counts,
      progress: counts.total ? Math.round((counts.delivered / counts.total) * 1000) / 10 : 0,
      lastDelivery,
    },
    shipments,
    previous,
  };
}
